import { logger } from "@/shared/lib/logger";
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Mail, Phone } from 'lucide-react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useFormDirty } from '@/shared/hooks/useFormDirty';
import { useUnsavedChanges } from '@/shared/hooks/useUnsavedChanges';
import { useConfirmDialog } from '@/shared/hooks/useConfirmDialog';
import { useCompanyPreferences } from '../hooks/useCompanyPreferences';
import { useProviderRequiredFields } from '../hooks/useProviderRequiredFields';
import { NewProviderPayload } from '../types/providers.types';
import { useProviders } from '../hooks/useProviders';
import { AppRoutes } from '@/app/routes';
import { useLocationSearch } from '@/features/admin/hooks/useLocationSearch';
import { Modal, ModalSize } from '@/shared/components/ui/Modal';
import { Button, ButtonVariant } from '@/shared/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Select } from '@/shared/components/ui/Select';
import Checkbox from '@/shared/ui/Checkbox';
import { DatePickerWithCalendar } from '@/shared/components/ui/DatePickerWithCalendar';

interface NewProviderModalProps {
    isOpen: boolean;
    onClose: () => void;
    onProviderCreated: (provider: { id: string; name: string }) => void;
}

const FormField: React.FC<{ label: string; required?: boolean; children: React.ReactNode; className?: string }> = ({ label, required, children, className }) => (
    <div className={className}>
      <label className="block text-sm font-medium text-text mb-1" title={label}>
        {label} {required && <span className="text-error">*</span>}
      </label>
      {children}
    </div>
  );

const initialFormData: NewProviderPayload = {
    name: '',
    code: '',
    edi_billing_provider_tax_identifier: '',
    eligibility_start: '',
    eligibility_end: '',
    credentialing_id: '',
    exempted_from_location_codes: false,
    provider_type: 'NORMAL',
    external_provider_type: 'NONE',
    transportation_subcontractor: false,
    contact_name: '',
    contact_email: '',
    contact_phone1: '',
    contact_phone2: '',
    street_address: '',
    city: '',
    postcode: '',
    preference: { id: 0 },
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const debounce = <F extends (...args: any[]) => any>(func: F, delay: number) => {
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    return (...args: Parameters<F>): void => {
        if (timeoutId) {
            clearTimeout(timeoutId);
        }
        timeoutId = setTimeout(() => {
            func(...args);
        }, delay);
    };
};

const hasInvalidDateRange = (startDate?: string, endDate?: string): boolean =>
    Boolean(startDate && endDate && startDate > endDate);

const NewProviderModal: React.FC<NewProviderModalProps> = ({ isOpen, onClose, onProviderCreated }) => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { preferences, isLoading } = useCompanyPreferences();
    const { createProvider, checkProviderExists, checkExternalProviderTypeUnique } = useProviders();
    const { isFieldRequired, getRequiredFieldKeys } = useProviderRequiredFields();
    const [formData, setFormData] = useState<NewProviderPayload>(initialFormData);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const skipBlockRef = useRef(false);

    // City autocomplete state
    const [citySearchTerm, setCitySearchTerm] = useState('');
    const [showCityDropdown, setShowCityDropdown] = useState(false);
    const [citySearchTimeout, setCitySearchTimeout] = useState<ReturnType<typeof setTimeout> | null>(null);
    const cityDropdownRef = useRef<HTMLDivElement>(null);
    const cityInputRef = useRef<HTMLInputElement>(null);
    const [cityDropdownPos, setCityDropdownPos] = useState({ top: 0, left: 0, width: 0 });

    // Zip code autocomplete state
    const [zipSearchTerm, setZipSearchTerm] = useState('');
    const [showZipDropdown, setShowZipDropdown] = useState(false);
    const [zipSearchTimeout, setZipSearchTimeout] = useState<ReturnType<typeof setTimeout> | null>(null);
    const zipDropdownRef = useRef<HTMLDivElement>(null);
    const zipInputRef = useRef<HTMLInputElement>(null);
    const [zipDropdownPos, setZipDropdownPos] = useState({ top: 0, left: 0, width: 0 });

    // Update dropdown positions when open
    useEffect(() => {
        if (showCityDropdown && cityInputRef.current) {
            const rect = cityInputRef.current.getBoundingClientRect();
            setCityDropdownPos({ top: rect.bottom + 2, left: rect.left, width: rect.width });
        }
    }, [showCityDropdown]);

    useEffect(() => {
        if (showZipDropdown && zipInputRef.current) {
            const rect = zipInputRef.current.getBoundingClientRect();
            setZipDropdownPos({ top: rect.bottom + 2, left: rect.left, width: rect.width });
        }
    }, [showZipDropdown]);

    const { results: citySuggestions, isLoading: isLoadingCities } = useLocationSearch({
        searchTerm: citySearchTerm,
        enabled: citySearchTerm.length >= 2,
        field: 'city',
    });

    const { results: zipCodes, isLoading: isLoadingZipCodes } = useLocationSearch({
        searchTerm: zipSearchTerm,
        enabled: zipSearchTerm.length >= 2,
        field: 'postcode',
    });

    const checkProviderName = useCallback(
        debounce(async (name: string) => {
            if (!name) return;
            try {
                const exists = await checkProviderExists(name);
                if (exists) {
                    setErrors(prev => ({ ...prev, name: t('providers.newProviderModal.errors.nameExists') }));
                }
            } catch (error) {
                logger.error("Failed to check provider name", error);
            }
        }, 500),
        [checkProviderExists, t]
    );

    const initialFormState = useMemo(() => {
        const mediumPriority = preferences.find(pref => pref.name.toLowerCase() === 'medium');
        return {
            ...initialFormData,
            preference: mediumPriority ? { id: mediumPriority.id } : { id: 0 }
        };
    }, [preferences]);

    const isDirty = useFormDirty({
        initialValues: initialFormState as unknown as Record<string, unknown>,
        currentValues: formData as unknown as Record<string, unknown>,
        enableTracking: isOpen,
    });

    useUnsavedChanges({ isDirty, enabled: isOpen, overrideRef: skipBlockRef });
    const { confirm, ConfirmDialog } = useConfirmDialog();

    // Close city/zip dropdowns when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (cityDropdownRef.current && !cityDropdownRef.current.contains(event.target as Node)) {
                setShowCityDropdown(false);
            }
            if (zipDropdownRef.current && !zipDropdownRef.current.contains(event.target as Node)) {
                setShowZipDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        if (isOpen) {
            setFormData(initialFormState);
            setErrors({});
            setCitySearchTerm('');
            setShowCityDropdown(false);
            setZipSearchTerm('');
            setShowZipDropdown(false);
        }
    }, [isOpen, initialFormState]);

    const handleCancel = () => {
        setFormData(initialFormState);
        setErrors({});
        onClose();
    };

    const handleCloseRequest = async () => {
        if (isDirty) {
            if (await confirm({ message: t('common.unsavedChangesPrompt', 'You have unsaved changes. Are you sure you want to discard them?') })) {
                handleCancel();
            }
        } else {
            handleCancel();
        }
    };

    const validate = () => {
        const newErrors: Record<string, string> = {};
        const requiredKeys = getRequiredFieldKeys();

        requiredKeys.forEach((key: string) => {
            const value = formData[key as keyof NewProviderPayload];
            let isMissing = false;

            if (key === 'preference.id') {
                if (!formData.preference?.id) {
                    isMissing = true;
                }
            } else {
                if (value === undefined || value === null) {
                    isMissing = true;
                } else if (typeof value === 'string' && value.trim() === '') {
                    isMissing = true;
                }
            }

            if (isMissing) {
                const fieldName = (key === 'preference')
                    ? t('providers.newProviderModal.priority')
                    : t(`providers.newProviderModal.${key}`, { defaultValue: key });
                newErrors[key] = t('common.errors.fieldRequired', { field: fieldName });
            }
        });

        // Email format validation (only when a value is present)
        if (formData.contact_email && formData.contact_email.trim()) {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(formData.contact_email.trim())) {
                newErrors.contact_email = t('common.errors.invalidEmail', 'Please enter a valid email address');
            }
        }

        if (hasInvalidDateRange(formData.eligibility_start ?? '', formData.eligibility_end ?? '')) {
            const message = t(
                'administration.providers.validation.endDateMustBeAfterStartDate',
                'End date must be after or equal to start date'
            );
            newErrors.eligibility_start = message;
            newErrors.eligibility_end = message;
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value, type } = e.target;
        if (name === 'provider_type') {
            // Changing provider_type clears the external_provider_type error too
            setErrors(prev => ({ ...prev, provider_type: '', external_provider_type: '' }));
        } else {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
        if (name === 'name') {
            checkProviderName(value);
        }

        // Strip non-numeric characters for phone fields
        if (name === 'contact_phone1' || name === 'contact_phone2') {
            const numericValue = value.replace(/\D/g, '');
            setFormData(prev => ({ ...prev, [name]: numericValue }));
            return;
        }

        if (name === 'preference') {
            setFormData(prev => ({ ...prev, preference: { id: Number(value) } }));
        } else {
            const checked = (e.target as HTMLInputElement).checked;
            setFormData(prev => ({
                ...prev,
                [name]: type === 'checkbox' ? checked : value,
            }));
        }
    };

    const handleCityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setFormData(prev => ({ ...prev, city: value }));
        setErrors(prev => ({ ...prev, city: '' }));

        if (citySearchTimeout) {
            clearTimeout(citySearchTimeout);
        }

        if (value.length >= 2) {
            const timeout = setTimeout(() => {
                setCitySearchTerm(value);
                setShowCityDropdown(true);
            }, 300);
            setCitySearchTimeout(timeout);
        } else {
            setCitySearchTerm('');
            setShowCityDropdown(false);
        }
    };

    const handlePostcodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const raw = e.target.value;
        setFormData(prev => ({ ...prev, postcode: raw }));
        setErrors(prev => ({ ...prev, postcode: '' }));

        if (zipSearchTimeout) {
            clearTimeout(zipSearchTimeout);
        }

        if (raw.length >= 2) {
            const timeout = setTimeout(() => {
                setZipSearchTerm(raw);
                setShowZipDropdown(true);
            }, 300);
            setZipSearchTimeout(timeout);
        } else {
            setZipSearchTerm('');
            setShowZipDropdown(false);
        }
    };

    // Common handler: shows inline "Too Short" error when Backspace is pressed on an already-empty field
    const handleEmptyBackspace = useCallback(
        (e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === 'Backspace' && !e.currentTarget.value) {
                const fieldName = e.currentTarget.name;
                setErrors(prev => ({
                    ...prev,
                    [fieldName]: t('providers.newProviderModal.tooShort', 'Too Short'),
                }));
            }
        },
        [t]
    );


    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        // Run sync validation first — sets field errors and returns false if any exist
        const syncValid = validate();
        if (!syncValid) {
            return;
        }

        // Reject if a debounced name-check already flagged a duplicate
        // (validate() doesn't check this; it's set asynchronously by checkProviderName)
        if (errors.name) {
            return;
        }

        setIsSubmitting(true);
        try {
            // Re-check name uniqueness at submit time (covers fast typists who outrun the debounce)
            if (formData.name) {
                const exists = await checkProviderExists(formData.name);
                if (exists) {
                    setErrors(prev => ({ ...prev, name: t('providers.newProviderModal.errors.nameExists') }));
                    return;
                }
            }

            // Validate unique external_provider_type (ECOPD-14326)
            if (formData.provider_type === 'EXTERNAL_PROVIDER' && formData.external_provider_type && formData.external_provider_type !== 'NONE') {
                const conflictingProviderName = await checkExternalProviderTypeUnique(formData.external_provider_type);
                if (conflictingProviderName) {
                    setErrors(prev => ({
                        ...prev,
                        external_provider_type: `External provider type has already been taken by "${conflictingProviderName}"`,
                    }));
                    return;
                }
            }

            const createdProvider = await createProvider(formData);
            const rawId = createdProvider?.successes?.[0]?.resource_id ?? createdProvider?.successes?.[0]?.id ?? createdProvider?.id;
            const createdId = rawId ? String(rawId) : '';
            const createdName = formData.name ?? '';
            skipBlockRef.current = true;
            handleCancel();
            if (createdId) {
                navigate(AppRoutes.admin.providerDetails(createdId));
            }
            onProviderCreated({ id: createdId, name: createdName });
        } catch (error) {
            logger.error("Failed to create provider", error);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleCloseRequest}
            title={t('providers.newProviderModal.title')}
            size={ModalSize.Xl}
            data-testid="new-provider-modal"
            preventCloseOnBackdrop={true}
        >
            <form onSubmit={handleSubmit} className="space-y-6" aria-label="New provider">
                {/* General Section */}
                <div>
                    <h3 className="text-lg font-medium text-foreground mb-4 pb-3 border-b border-border">
                        {t('providers.newProviderModal.general')}
                    </h3>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <FormField
                            label={t('providers.newProviderModal.name')}
                            required={isFieldRequired('name')}
                        >
                            <Input
                                id="provider-name"
                                type="text"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                disabled={isSubmitting}
                                data-testid="provider-name-input"
                                aria-required={isFieldRequired('name')}
                                aria-label={t('providers.newProviderModal.name')}
                                className={errors.name ? 'border-error' : ''}
                            />
                            {errors.name && <p role="alert" className="text-error text-xs mt-1">{errors.name}</p>}
                        </FormField>
                        <FormField
                            label={t('providers.newProviderModal.taxId')}
                            required={isFieldRequired('edi_billing_provider_tax_identifier')}
                        >
                            <Input
                                id="tax-id"
                                type="text"
                                name="edi_billing_provider_tax_identifier"
                                value={formData.edi_billing_provider_tax_identifier ?? ''}
                                onChange={handleChange}
                                aria-required={isFieldRequired('edi_billing_provider_tax_identifier')}
                                disabled={isSubmitting}
                                data-testid="tax-id-input"
                                className={errors.edi_billing_provider_tax_identifier ? 'border-error' : ''}
                            />
                            {errors.edi_billing_provider_tax_identifier && <p role="alert" className="text-error text-xs mt-1">{errors.edi_billing_provider_tax_identifier}</p>}
                        </FormField>
                        <FormField
                            label={t('providers.newProviderModal.eligibilityStart')}
                            required={isFieldRequired('eligibility_start')}
                        >
                            <DatePickerWithCalendar
                                id="eligibility-start"
                                value={formData.eligibility_start ?? ''}
                                onChange={(val) => {
                                    setFormData(prev => ({ ...prev, eligibility_start: val }));
                                    setErrors(prev => ({ ...prev, eligibility_start: '' }));
                                }}
                                disabled={isSubmitting}
                                required={isFieldRequired('eligibility_start')}
                                error={errors.eligibility_start}
                                data-testid="eligibility-start-input"
                            />
                        </FormField>
                        <FormField
                            label={t('providers.newProviderModal.eligibilityEnd')}
                            required={isFieldRequired('eligibility_end')}
                        >
                            <DatePickerWithCalendar
                                id="eligibility-end"
                                value={formData.eligibility_end ?? ''}
                                onChange={(val) => {
                                    setFormData(prev => ({ ...prev, eligibility_end: val }));
                                    setErrors(prev => ({ ...prev, eligibility_end: '' }));
                                }}
                                disabled={isSubmitting}
                                required={isFieldRequired('eligibility_end')}
                                error={errors.eligibility_end}
                                data-testid="eligibility-end-input"
                            />
                        </FormField>
                        <FormField
                            label={t('providers.newProviderModal.credentialingId')}
                            required={isFieldRequired('credentialing_id')}
                        >
                            <Input
                                id="credentialing-id"
                                type="text"
                                name="credentialing_id"
                                value={formData.credentialing_id ?? ''}
                                onChange={handleChange}
                                aria-required={isFieldRequired('credentialing_id')}
                                disabled={isSubmitting}
                                data-testid="credentialing-id-input"
                                className={errors.credentialing_id ? 'border-error' : ''}
                            />
                            {errors.credentialing_id && <p role="alert" className="text-error text-xs mt-1">{errors.credentialing_id}</p>}
                        </FormField>
                        <FormField
                            label={t('providers.providerInfo.otherProviderId')}
                            required={isFieldRequired('code')}
                        >
                            <Input
                                id="code"
                                type="text"
                                name="code"
                                value={formData.code ?? ''}
                                onChange={handleChange}
                                aria-required={isFieldRequired('code')}
                                disabled={isSubmitting}
                                data-testid="code-input"
                                className={errors.code ? 'border-error' : ''}
                            />
                            {errors.code && <p role="alert" className="text-error text-xs mt-1">{errors.code}</p>}
                        </FormField>

                        {/* Exempt from Location Codes */}
                        <div className="md:col-span-2 flex items-center mt-6">
                            <Checkbox
                                id="exempt-location-codes"
                                name="exempted_from_location_codes"
                                checked={formData.exempted_from_location_codes}
                                onChange={handleChange}
                                disabled={isSubmitting}
                                data-testid="exempt-location-codes-checkbox"
                                label={t('providers.newProviderModal.exemptFromLocationCodes')}
                            />
                        </div>
                    </div>
                </div>

                {/* Provider Types Section */}
                <div>
                    <h3 className="text-lg font-medium text-foreground mb-4 pb-3 border-b border-border">
                        {t('providers.newProviderModal.providerTypes')}
                    </h3>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <FormField
                            label={t('providers.newProviderModal.type')}
                            required={isFieldRequired('provider_type')}
                        >
                            <Select
                                id="provider-type"
                                label=""
                                name="provider_type"
                                value={formData.provider_type}
                                onChange={handleChange}
                                aria-required={isFieldRequired('provider_type')}
                                disabled={isSubmitting}
                                data-testid="provider-type-select"
                                useNative={true}
                                options={[
                                    { value: 'EXTERNAL_PROVIDER', label: t('providers.sidebar.external') },
                                    { value: 'INDEPENDENT_PROVIDER', label: t('providers.sidebar.independent') },
                                    { value: 'NORMAL', label: t('providers.sidebar.normal') },
                                    { value: 'PUBLIC_TRANSPORT', label: t('providers.sidebar.publicTransportation') },
                                    { value: 'RESTRICTED', label: t('providers.sidebar.restricted') },
                                    { value: 'TRIPS_ONLY', label: t('providers.sidebar.tripsOnly') }
                                ]}
                                className={errors.provider_type ? 'border-error' : ''}
                            />
                            {errors.provider_type && <p role="alert" className="text-error text-xs mt-1">{errors.provider_type}</p>}
                        </FormField>
                        
                        {formData.provider_type === 'EXTERNAL_PROVIDER' && (
                            <div>
                                <Select
                                    id="external-provider-type"
                                    label={<>{t('providers.newProviderModal.externalProviderType', 'External Provider Type')} {isFieldRequired('external_provider_type') && <span className="text-error" aria-hidden="true">*</span>}</>}
                                    labelTitle={t('providers.newProviderModal.externalProviderType', 'External Provider Type')}
                                    name="external_provider_type"
                                    value={formData.external_provider_type ?? ''}
                                    onChange={handleChange}
                                    aria-required={isFieldRequired('external_provider_type')}
                                    disabled={isSubmitting}
                                    data-testid="external-provider-type-select"
                                    useNative={true}
                                    aria-describedby={errors.external_provider_type ? 'external-provider-type-error' : undefined}
                                    aria-invalid={errors.external_provider_type ? true : undefined}
                                    options={[
                                        { value: 'NONE', label: "None" },
                                        { value: 'TEST', label: "Test" },
                                        { value: 'UZURV',  label : "Uzurv" },
                                        { value: 'TSS', label: "TSS"},
                                        { value: 'ROUNDTRIP', label: "ROUNDTRIP" },
                                        { value: 'LYFT', label: "Lyft"},
                                        { value: 'ICABBI', label: "iCabbi" },
                                        { value: 'WELLTRANS', label: "Welltrans" },
                                        { value: 'UBER', label: "Uber" }
                                    ]}
                                    className={errors.external_provider_type ? 'border-error' : ''}
                                />
                                {errors.external_provider_type && (
                                    <p id="external-provider-type-error" role="alert" className="text-error text-xs mt-1">
                                        {errors.external_provider_type}
                                    </p>
                                )}
                            </div>
                        )}
                        <FormField
                            label={t('providers.newProviderModal.priority')}
                            required={isFieldRequired('preference.id')}
                        >
                            <Select
                                id="priority"
                                label=""
                                name="preference"
                                value={formData.preference?.id ?? ''}
                                onChange={handleChange}
                                aria-required={isFieldRequired('preference')}
                                disabled={isLoading || isSubmitting}
                                data-testid="priority-select"
                                useNative={true}
                                options={[
                                    { value: '', label: t('providers.newProviderModal.selectPriority') },
                                    ...preferences.map((pref) => ({ value: pref.id, label: pref.name }))
                                ]}
                                className={errors.preference ? 'border-error' : ''}
                            />
                            {errors.preference && <p role="alert" className="text-error text-xs mt-1">{errors.preference}</p>}
                        </FormField>

                        {/* Subcontractor */}
                        <div className="flex items-center mt-6">
                            <Checkbox
                                id="subcontractor"
                                name="transportation_subcontractor"
                                checked={formData.transportation_subcontractor}
                                onChange={handleChange}
                                disabled={isSubmitting}
                                data-testid="subcontractor-checkbox"
                                label={t('providers.newProviderModal.subcontractor')}
                            />
                        </div>
                    </div>
                </div>

                {/* Contact Section */}
                <div>
                    <h3 className="text-lg font-medium text-foreground mb-4 pb-3 border-b border-border">
                        {t('providers.newProviderModal.contact')}
                    </h3>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <FormField
                            label={t('providers.newProviderModal.contactName')}
                            required={isFieldRequired('contact_name')}
                        >
                            <Input
                                id="contact-name"
                                type="text"
                                name="contact_name"
                                value={formData.contact_name ?? ''}
                                onChange={handleChange}
                                aria-required={isFieldRequired('contact_name')}
                                disabled={isSubmitting}
                                data-testid="contact-name-input"
                                className={errors.contact_name ? 'border-error' : ''}
                            />
                            {errors.contact_name && <p role="alert" className="text-error text-xs mt-1">{errors.contact_name}</p>}
                        </FormField>
                        <FormField
                            label={t('providers.newProviderModal.email')}
                            required={isFieldRequired('contact_email')}
                        >
                            <div className="relative">
                                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                                    <Mail className="h-4 w-4 text-text-muted" />
                                </div>
                                <Input
                                    id="email"
                                    type="email"
                                    name="contact_email"
                                    value={formData.contact_email ?? ''}
                                    onChange={handleChange}
                                    aria-required={isFieldRequired('contact_email')}
                                    disabled={isSubmitting}
                                    data-testid="email-input"
                                    className={`pr-9 ${errors.contact_email ? 'border-error' : ''}`}
                                />
                            </div>
                            {errors.contact_email && <p role="alert" className="text-error text-xs mt-1">{errors.contact_email}</p>}
                        </FormField>
                        <FormField
                            label={t('providers.newProviderModal.contactPhone1')}
                            required={isFieldRequired('contact_phone1')}
                        >
                            <div className="relative">
                                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                                    <Phone className="h-4 w-4 text-text-muted" />
                                </div>
                                <Input
                                    id="phone1"
                                    type="tel"
                                    name="contact_phone1"
                                    value={formData.contact_phone1 ?? ''}
                                    onChange={handleChange}
                                    aria-required={isFieldRequired('contact_phone1')}
                                    disabled={isSubmitting}
                                    data-testid="phone1-input"
                                    className={`pr-9 ${errors.contact_phone1 ? 'border-error' : ''}`}
                                />
                            </div>
                            {errors.contact_phone1 && <p role="alert" className="text-error text-xs mt-1">{errors.contact_phone1}</p>}
                        </FormField>
                        <FormField
                            label={t('providers.newProviderModal.contactPhone2')}
                            required={isFieldRequired('contact_phone2')}
                        >
                            <div className="relative">
                                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                                    <Phone className="h-4 w-4 text-text-muted" />
                                </div>
                                <Input
                                    id="phone2"
                                    type="tel"
                                    name="contact_phone2"
                                    value={formData.contact_phone2 ?? ''}
                                    onChange={handleChange}
                                    aria-required={isFieldRequired('contact_phone2')}
                                    disabled={isSubmitting}
                                    data-testid="phone2-input"
                                    className={`pr-9 ${errors.contact_phone2 ? 'border-error' : ''}`}
                                />
                            </div>
                            {errors.contact_phone2 && <p role="alert" className="text-error text-xs mt-1">{errors.contact_phone2}</p>}
                        </FormField>
                        <FormField
                            label={t('providers.newProviderModal.street')}
                            required={isFieldRequired('street_address')}
                        >
                            <Input
                                id="street"
                                type="text"
                                name="street_address"
                                value={formData.street_address ?? ''}
                                onChange={handleChange}
                                aria-required={isFieldRequired('street_address')}
                                disabled={isSubmitting}
                                data-testid="street-input"
                                className={errors.street_address ? 'border-error' : ''}
                            />
                            {errors.street_address && <p role="alert" className="text-error text-xs mt-1">{errors.street_address}</p>}
                        </FormField>
                        <FormField
                            label={t('providers.newProviderModal.city')}
                            required={isFieldRequired('city')}
                        >
                            <div className="relative" ref={cityDropdownRef}>
                                <input
                                    ref={cityInputRef}
                                    id="city"
                                    type="text"
                                    name="city"
                                    value={formData.city ?? ''}
                                    onChange={handleCityChange}
                                    onKeyDown={handleEmptyBackspace}
                                    aria-required={isFieldRequired('city')}
                                    disabled={isSubmitting}
                                    data-testid="city-input"
                                    autoComplete="off"
                                    className={`w-full px-3 h-9 border rounded-md bg-surface text-text focus:outline-none focus:ring-2 focus:ring-primary text-sm ${errors.city ? 'border-error' : 'border-border'}`}
                                />
                                {showCityDropdown && (citySuggestions.length > 0 || isLoadingCities) && ReactDOM.createPortal(
                                    <div className="fixed z-[1100] bg-surface border border-border rounded-md shadow-lg max-h-60 overflow-y-auto" style={{ top: cityDropdownPos.top, left: cityDropdownPos.left, width: cityDropdownPos.width }}>
                                        {isLoadingCities ? (
                                            <div className="px-4 py-3 text-sm text-text-muted">{t('common.searching', 'Searching...')}</div>
                                        ) : (
                                            citySuggestions.map((city: string, index: number) => (
                                                <button
                                                    key={index}
                                                    type="button"
                                                    onMouseDown={(e) => {
                                                        e.preventDefault();
                                                        setFormData(prev => ({ ...prev, city }));
                                                        setErrors(prev => ({ ...prev, city: '' }));
                                                        setCitySearchTerm('');
                                                        setShowCityDropdown(false);
                                                    }}
                                                    className="w-full text-left px-4 py-3 hover:bg-surface-alt focus:bg-surface-alt focus:outline-none border-b border-border last:border-b-0"
                                                >
                                                    <div className="text-sm font-medium text-text">{city}</div>
                                                </button>
                                            ))
                                        )}
                                    </div>,
                                    document.body
                                )}
                            </div>
                            {errors.city && <p role="alert" className="text-error text-xs mt-1">{errors.city}</p>}
                        </FormField>
                        <FormField
                            label={t('providers.newProviderModal.zipCode')}
                            required={isFieldRequired('postcode')}
                        >
                            <div className="relative" ref={zipDropdownRef}>
                                <input
                                    ref={zipInputRef}
                                    id="postcode"
                                    type="text"
                                    name="postcode"
                                    value={formData.postcode ?? ''}
                                    onChange={handlePostcodeChange}
                                    onKeyDown={handleEmptyBackspace}
                                    aria-required={isFieldRequired('postcode')}
                                    disabled={isSubmitting}
                                    data-testid="postcode-input"
                                    autoComplete="off"
                                    className={`w-full px-3 h-9 border rounded-md bg-surface text-text focus:outline-none focus:ring-2 focus:ring-primary text-sm ${errors.postcode ? 'border-error' : 'border-border'}`}
                                />
                                {showZipDropdown && (zipCodes.length > 0 || isLoadingZipCodes) && ReactDOM.createPortal(
                                    <div className="fixed z-[1100] bg-surface border border-border rounded-md shadow-lg max-h-60 overflow-y-auto" style={{ top: zipDropdownPos.top, left: zipDropdownPos.left, width: zipDropdownPos.width }}>
                                        {isLoadingZipCodes ? (
                                            <div className="px-4 py-3 text-sm text-text-muted">{t('common.searching', 'Searching...')}</div>
                                        ) : (
                                            zipCodes.map((zip: string, index: number) => (
                                                <button
                                                    key={index}
                                                    type="button"
                                                    onMouseDown={(e) => {
                                                        e.preventDefault();
                                                        setFormData(prev => ({ ...prev, postcode: zip }));
                                                        setErrors(prev => ({ ...prev, postcode: '' }));
                                                        setZipSearchTerm('');
                                                        setShowZipDropdown(false);
                                                    }}
                                                    className="w-full text-left px-4 py-3 hover:bg-surface-alt focus:bg-surface-alt focus:outline-none border-b border-border last:border-b-0"
                                                >
                                                    <div className="text-sm font-medium text-text">{zip}</div>
                                                </button>
                                            ))
                                        )}
                                    </div>,
                                    document.body
                                )}
                            </div>
                            {errors.postcode && <p role="alert" className="text-error text-xs mt-1">{errors.postcode}</p>}
                        </FormField>
                    </div>
                </div>

                {/* Footer Actions */}
                <div className="flex justify-end items-center pt-6 border-t border-border space-x-3">
                    <Button
                        type="button"
                        variant={ButtonVariant.Outline}
                        onClick={handleCancel}
                        disabled={isSubmitting}
                        data-testid="cancel-button"
                    >
                        {t('providers.newProviderModal.cancel')}
                    </Button>
                    <Button
                        type="submit"
                        variant={ButtonVariant.Primary}
                        disabled={isSubmitting}
                        data-testid="submit-button"
                    >
                        {isSubmitting ? t('providers.newProviderModal.creating', 'Creating...') : t('providers.newProviderModal.create')}
                    </Button>
                </div>
            </form>
            <ConfirmDialog />
        </Modal>
    );
};

export default NewProviderModal;
